import { useParams, Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, MapPin, Building, Users, Trophy, Landmark, BarChart, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { laNeighborhoods } from "@/data/laNeighborhoods";
import { additionalNeighborhoods } from "@/data/moreNeighborhoods";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import { generateArticleSchema, generateFaqSchema, getCanonicalUrl, formatSlug } from "@/utils/seoUtils";
import PageTransition from "@/components/PageTransition";
import Navbar from "@/components/Navbar";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";

const allNeighborhoods = [...laNeighborhoods, ...additionalNeighborhoods];

const NeighborhoodPage = () => {
  const { id } = useParams<{ id: string }>();
  const [neighborhood, setNeighborhood] = useState<typeof allNeighborhoods[0] | null>(null);
  const [useNewUrl, setUseNewUrl] = useState(false);
  
  useEffect(() => {
    if (id) {
      const foundNeighborhood = allNeighborhoods.find(n => n.id === id);
      if (foundNeighborhood) {
        setNeighborhood(foundNeighborhood);
        
        const isLegacyPath = window.location.pathname.includes('/neighborhood/');
        setUseNewUrl(!isLegacyPath);
      }
    }
  }, [id]);

  if (!neighborhood) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Neighborhood not found</h1>
          <p className="mb-6">Sorry, we couldn't find information about this neighborhood.</p>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const seoUrl = `/los-angeles-seo/${neighborhood.id}`;
  
  if (window.location.pathname.includes('/neighborhood/') && !window.location.pathname.includes('/los-angeles-seo/')) {
    return <Navigate to={seoUrl} replace />;
  }
  
  const title = `Best SEO Services in ${neighborhood.name}, Los Angeles | ${neighborhood.keyIndustries[0]} SEO Experts`;
  
  const description = `Boost your ${neighborhood.name} business visibility with our specialized local SEO services. #1 rated ${neighborhood.name} SEO agency helping ${neighborhood.keyIndustries.slice(0, 2).join(" & ")} businesses rank higher. ${neighborhood.competitiveLandscape} competition area experts.`;
  
  const articleSchema = generateArticleSchema({
    title,
    description,
    image: "/og-image.png",
    publishDate: "2024-01-01T08:00:00+08:00",
    lastModified: "2024-06-01T08:00:00+08:00",
    author: { name: "Los Angeles SEO Group" },
    url: getCanonicalUrl(seoUrl),
    keywords: `SEO ${neighborhood.name}, ${neighborhood.name} SEO Agency, Local SEO ${neighborhood.name}, ${neighborhood.name} Digital Marketing, ${neighborhood.keyIndustries.join(", ")}`
  });
  
  const faqSchema = generateFaqSchema([
    {
      question: `Why do businesses in ${neighborhood.name} need specialized SEO services?`,
      answer: `${neighborhood.name} businesses face unique challenges in the digital landscape due to the competitive nature of the ${neighborhood.region} Los Angeles region and the specific needs of the ${neighborhood.keyIndustries.join(", ")} industries. Our specialized SEO services are tailored to help local businesses stand out in local searches and attract more customers from both the neighborhood and broader LA area.`
    },
    {
      question: `How long does it take to see SEO results in ${neighborhood.name}?`,
      answer: `While every business in ${neighborhood.name} is different, most of our clients begin to see improvements in rankings within 3-6 months. Local SEO can sometimes show faster results, especially for businesses targeting specific ${neighborhood.name} keywords with lower competition. We provide monthly reports to track your progress.`
    },
    {
      question: `What makes Los Angeles SEO Group different from other agencies serving ${neighborhood.name}?`,
      answer: `We have deep knowledge of the ${neighborhood.name} market, including local consumer behavior, competition landscape, and industry trends specific to the ${neighborhood.region} region of Los Angeles. Our team includes experts who live and work in the area, providing authentic local insights that national agencies can't match.`
    }
  ]);

  const enhancedKeywords = `${neighborhood.name} SEO Agency, SEO Services ${neighborhood.name}, Local SEO ${neighborhood.name}, 
    ${neighborhood.keyIndustries.map(industry => `${industry} SEO ${neighborhood.name}`).join(", ")}, 
    ${neighborhood.region} Los Angeles SEO, Best SEO Company ${neighborhood.name}, 
    ${neighborhood.name} Search Engine Optimization`;

  return (
    <PageTransition>
      <SEO 
        seo={{
          title,
          description,
          ogImage: "/og-image.png",
          ogType: "article",
          jsonLd: [articleSchema, faqSchema],
          canonical: getCanonicalUrl(seoUrl),
          keywords: enhancedKeywords,
          author: "Los Angeles SEO Group",
          publishedTime: "2024-01-01T08:00:00+08:00",
          modifiedTime: "2024-06-01T08:00:00+08:00"
        }}
        url={getCanonicalUrl(seoUrl)}
      />
      
      <Navbar />
      
      <div className="bg-background/80 backdrop-blur-md border-b border-border/30 py-3 sticky top-[72px] z-40">
        <div className="page-container">
          <div className="flex items-center justify-between">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm">
                    {neighborhood.name} SEO Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {neighborhood.keyIndustries.slice(0, 6).map((industry, i) => (
                        <li key={i} className="row-span-1">
                          <NavigationMenuLink asChild>
                            <a
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                              href={`#${industry.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <div className="text-sm font-medium leading-none">{industry} SEO</div>
                              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                                Specialized SEO strategies for {industry} businesses in {neighborhood.name}
                              </p>
                            </a>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to={`#services`} className={navigationMenuTriggerStyle()}>
                    Our Services
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to={`#strategy`} className={navigationMenuTriggerStyle()}>
                    SEO Strategy
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to={`#faq`} className={navigationMenuTriggerStyle()}>
                    FAQ
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to={`#contact`} className={navigationMenuTriggerStyle()}>
                    Get a Quote
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <Button asChild variant="outline" size="sm">
              <Link to="/neighborhoods" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                <span>All Neighborhoods</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <section className="bg-soft-gradient py-16 md:py-24">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge variant="soft" className="mb-4">
                  {neighborhood.region} Los Angeles
                </Badge>
                
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  #1 SEO Services in <span className="text-gradient bg-primary-gradient">{neighborhood.name}</span>
                </h1>
                
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-indigo-500" />
                    <span>{neighborhood.businessCount.toLocaleString()}+ Businesses</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-500" />
                    <span>{neighborhood.population.toLocaleString()}+ Residents</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart className="h-5 w-5 text-indigo-500" />
                    <span>Avg. Ranking: {neighborhood.averageRanking}</span>
                  </div>
                </div>
                
                <p className="text-lg text-foreground/80 mb-8">
                  Los Angeles SEO Group provides specialized search engine optimization services for businesses in {neighborhood.name}, helping you increase visibility, attract more local customers, and grow your business. Our team understands the unique aspects of the {neighborhood.name} market and delivers tailored strategies that work.
                </p>
                
                <h2 id="services" className="text-2xl font-bold mb-4">Why {neighborhood.name} Businesses Need SEO</h2>
                <p className="mb-6">
                  {neighborhood.name} is known for {neighborhood.knownFor}, with a competitive landscape rated as {neighborhood.competitiveLandscape.toLowerCase()}. Local businesses need strong digital presence to stand out and reach their target audience effectively.
                </p>
                
                <h2 className="text-2xl font-bold mb-4">Our {neighborhood.name} SEO Services</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Local SEO strategies tailored for {neighborhood.name}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Google Business Profile optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Local link building with {neighborhood.name} businesses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Content marketing for {neighborhood.keyIndustries[0]} and {neighborhood.keyIndustries[1]}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Location-based keyword optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span>Competitive analysis of local {neighborhood.name} businesses</span>
                  </li>
                </ul>
              </motion.div>
              
              <Separator className="my-12" />
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <h2 className="text-2xl font-bold mb-6">Key Industries We Serve in {neighborhood.name}</h2>
                <div className="flex flex-wrap gap-2 mb-8">
                  {neighborhood.keyIndustries.map((industry, index) => (
                    <Badge key={index} variant={index % 2 === 0 ? "soft" : "outline"} className="text-sm">
                      {industry}
                    </Badge>
                  ))}
                </div>
                
                <h3 className="text-xl font-bold mb-4">Local Business Insights</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span><strong>Key Area:</strong> {neighborhood.name} has {neighborhood.businessCount.toLocaleString()}+ businesses competing for visibility in local search.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Trophy className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span><strong>Opportunity:</strong> The average business in {neighborhood.name} ranks {neighborhood.averageRanking}th in local search results, showing significant room for improvement.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Landmark className="h-5 w-5 text-indigo-500 mt-1 shrink-0" />
                    <span><strong>Local Context:</strong> Notable landmarks like {neighborhood.localLandmarks.slice(0, 2).join(" and ")} create unique local search contexts we can leverage.</span>
                  </li>
                </ul>
                
                <h3 className="text-xl font-bold mb-4">Notable Businesses in {neighborhood.name}</h3>
                <p className="mb-6">
                  {neighborhood.name} is home to businesses like {neighborhood.notableBusinesses.join(", ")}, showing the diverse economic landscape we understand and navigate for our SEO clients.
                </p>
              </motion.div>
              
              <Separator className="my-12" />
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <h2 id="strategy" className="text-2xl font-bold mb-6">{neighborhood.name} SEO Strategy</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <Card>
                    <CardHeader>
                      <CardTitle>Local Search Optimization</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>
                        We optimize your business for location-based searches specific to {neighborhood.name} and nearby areas. This includes Google Business Profile optimization, local citations, and neighborhood-specific keyword targeting.
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Community-Focused Content</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>
                        We create content that resonates with {neighborhood.name} residents and businesses, highlighting local events, landmarks like {neighborhood.localLandmarks[0]}, and addressing specific community needs.
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Competitor Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>
                        We analyze what works for successful {neighborhood.name} businesses in your industry and develop strategies to help you outperform them in local search rankings.
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Local Link Building</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>
                        We build relationships with other {neighborhood.name} businesses and organizations to create a strong local backlink profile that signals your relevance to the area.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
              
              <Separator className="my-12" />
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <h2 id="faq" className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Why do businesses in {neighborhood.name} need specialized SEO services?</h3>
                    <p className="text-foreground/80">
                      {neighborhood.name} businesses face unique challenges in the digital landscape due to the competitive nature of the {neighborhood.region} Los Angeles region and the specific needs of the {neighborhood.keyIndustries.join(", ")} industries. Our specialized SEO services are tailored to help local businesses stand out in local searches and attract more customers from both the neighborhood and broader LA area.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">How long does it take to see SEO results in {neighborhood.name}?</h3>
                    <p className="text-foreground/80">
                      While every business in {neighborhood.name} is different, most of our clients begin to see improvements in rankings within 3-6 months. Local SEO can sometimes show faster results, especially for businesses targeting specific {neighborhood.name} keywords with lower competition. We provide monthly reports to track your progress.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">What makes Los Angeles SEO Group different from other agencies serving {neighborhood.name}?</h3>
                    <p className="text-foreground/80">
                      We have deep knowledge of the {neighborhood.name} market, including local consumer behavior, competition landscape, and industry trends specific to the {neighborhood.region} region of Los Angeles. Our team includes experts who live and work in the area, providing authentic local insights that national agencies can't match.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="sticky top-[160px]"
              >
                <Card id="contact" className="bg-white dark:bg-card shadow-xl border-indigo-100 dark:border-indigo-900/30">
                  <CardHeader className="pb-2">
                    <CardTitle>Get {neighborhood.name} SEO Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-6 text-sm text-muted-foreground">
                      Fill out the form below to get a free consultation and learn how we can help your {neighborhood.name} business grow with SEO.
                    </p>
                    
                    <ContactForm neighborhoodName={neighborhood.name} />
                    
                    <div className="mt-6 space-y-3">
                      <h4 className="font-medium">Why choose us for {neighborhood.name} SEO:</h4>
                      <ul className="space-y-2 text-sm">
                        {neighborhood.conversionTips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-indigo-500 mt-1 shrink-0" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
};

export default NeighborhoodPage;
